import { useCallback, useMemo } from "react";
import { isEmpty, orderBy } from "lodash";
import { useFirestoreCollection } from "./useFirestoreCollection";
import {
  addDoc,
  getDocs,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { useFirestore } from "./useFirestore.web";

export type AreaLevelLookup = Map<string, number>;

export const useAreas = (projectId: string, objectId: string) => {
  const [tenantId, , , collection] = useFirestore();

  const [unsortedAreas, isLoading] = useFirestoreCollection(
    ["projects", projectId, "objects", objectId, "areas"].join("/")
  );

  const areas = useMemo(
    () => orderBy(unsortedAreas, [(a) => a.get("order")], ["asc"]),
    [unsortedAreas]
  );

  const levelForAreaId = useMemo(() => {
    const lookup: AreaLevelLookup = new Map();

    const rootElements = areas.filter((a) => a.get("parentId") == null);

    const assignLevel = (currentAreas: typeof areas, level: number) => {
      currentAreas.forEach((a) => {
        lookup.set(a.id, level);

        const children = areas.filter((x) => x.get("parentId") === a.id);
        assignLevel(children, level + 1);
      });
    };

    assignLevel(rootElements, 1);

    return lookup;
  }, [areas]);

  const addArea = useCallback(
    async (areaName: string, parentId: string | null, order?: number) => {
      if (isEmpty(areaName)) {
        return;
      }

      const collectionPath = [
        "projects",
        projectId,
        "objects",
        objectId,
        "areas",
      ].join("/");

      if (!order) {
        const q = query(
          collection(collectionPath),
          where("parentId", "==", parentId)
        );

        const existingAreas = await getDocs(q);
        order = existingAreas.size + 1;
      }

      return addDoc(collection(collectionPath), {
        title: areaName,
        parentId,
        order,
        createdAt: Timestamp.now(),
      });
    },
    [tenantId, projectId, objectId]
  );

  const rootAreas = useMemo(
    () => areas.filter((a) => levelForAreaId.get(a.id) === 1),
    [levelForAreaId]
  );

  return {
    areas,
    addArea,
    rootAreas,
    levelForAreaId,
    isLoading,
  };
};
